// frontend/src/components/ambassador/AmbassadorMonitor.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as api from '../../api';
import { fmt, fmtK, PayBadge, StatCard, Pager, CollapsibleCard, toDate } from '../common/CommonComponents';
import Leaderboard from '../common/Leaderboard';

function AmbassadorMonitor({ showAlert, user }) {
  const canManageAmbassadorFinance = !!user?.isBusinessOwner || (user?.role === "manager" && !user?.shopId);
  const [report, setReport] = useState([]);
  const [withdrawals, setWd] = useState([]);
  const [lbData, setLbData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [activeTab, setActiveTab] = useState("monitor");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("totalEarned");
  const [sortDirection, setSortDirection] = useState("desc");
  const [processingId, setProcessingId] = useState(null);

  const isMounted = useRef(true);

  // Silent refresh function - updates data without showing loading spinner
  const refreshDataSilently = useCallback(async () => {
    if (!isMounted.current) return;
    try {
      const [r, w, lb] = await Promise.all([
        api.getAmbassadorMonitor(),
        api.getWithdrawals(),
        api.getLeaderboard()
      ]);
      if (isMounted.current) {
        setReport(r.report || r);
        setWd(w);
        setLbData(lb);
      }
    } catch(e) {
      console.error("Silent refresh failed:", e);
    }
  }, []);

  // Initial load with spinner
  const initialLoad = useCallback(async () => {
    setLoading(true);
    try {
      const [r, w, lb] = await Promise.all([
        api.getAmbassadorMonitor(),
        api.getWithdrawals(),
        api.getLeaderboard()
      ]);
      if (isMounted.current) {
        setReport(r.report || r);
        setWd(w);
        setLbData(lb);
      }
    } catch(e) {
      showAlert("Failed to load", "error");
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  }, [showAlert]);

  useEffect(() => {
    isMounted.current = true;
    initialLoad();
    
    return () => {
      isMounted.current = false;
    };
  }, [initialLoad]);

  // Silent background refresh every 30 seconds (no UI disruption)
  useEffect(() => {
    if (isInitialLoad) return;
    
    const interval = setInterval(() => {
      refreshDataSilently();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [isInitialLoad, refreshDataSilently]);

  // Refresh when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && !isInitialLoad && isMounted.current) {
        refreshDataSilently();
      }
    };
    
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isInitialLoad, refreshDataSilently]);

  const handleWd = async (id, action) => {
    setProcessingId(id);
    try { 
      await api.processWithdrawal(id, action); 
      showAlert(action === "approve" ? "Withdrawal approved — expense auto-created!" : "Withdrawal rejected!", action === "approve" ? "success" : "error");
      // Immediate local update (optimistic)
      setWd(prev => prev.map(w => 
        w.withdrawalId === id ? { ...w, status: action === "approve" ? "Approved" : "Rejected" } : w
      ));
      // Then silent refresh
      refreshDataSilently();
    } catch(e) { 
      showAlert(e.message, "error"); 
    } finally {
      setProcessingId(null);
    }
  };

  const handleBulkApprove = async () => {
    const pendingWithdrawals = withdrawals.filter(w => w.status === "Pending");
    if (pendingWithdrawals.length === 0) {
      showAlert("No pending withdrawals to approve", "info");
      return;
    }
    if (!window.confirm(`Approve ${pendingWithdrawals.length} withdrawal request(s)?`)) return;
    
    let approved = 0;
    for (const w of pendingWithdrawals) {
      try {
        await api.processWithdrawal(w.withdrawalId, "approve");
        approved++;
        // Immediate local update
        setWd(prev => prev.map(pw => 
          pw.withdrawalId === w.withdrawalId ? { ...pw, status: "Approved" } : pw
        ));
      } catch(e) { console.error(e); }
    }
    showAlert(`${approved} withdrawal(s) approved!`, "success");
    refreshDataSilently();
  };

  const handleCommission = async (email, rate) => {
    try { 
      const r = await api.updateCommissionRate(email, Number(rate)); 
      showAlert(r.message || "Commission rate updated — applies to new orders only!", "success"); 
      refreshDataSilently();
    } catch(e) { 
      showAlert(e.message, "error"); 
    }
  };

  const handleReset = async (body) => {
    try { 
      await api.resetLeaderboard(body); 
      showAlert("Leaderboard reset! Winner crowned. Hall of Fame updated.", "success"); 
      refreshDataSilently();
    } catch(e) { 
      showAlert(e.message, "error"); 
    }
  };

  // Calculate summary stats
  const totalAmbassadors = report.length;
  const totalEarned = report.reduce((sum, a) => sum + (a.totalEarned || 0), 0);
  const totalAvailable = report.reduce((sum, a) => sum + (a.availableBalance || 0), 0);
  const pendingWithdrawals = withdrawals.filter(w => w.status === "Pending").length;
  const pendingAmount = withdrawals.filter(w => w.status === "Pending").reduce((sum, w) => sum + (w.amount || 0), 0);
  
  // Filter and sort report
  const filteredReport = report
    .filter(a => {
      if (searchTerm && !a.name?.toLowerCase().includes(searchTerm.toLowerCase()) && 
          !a.email?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      let valA = a[sortField] || 0;
      let valB = b[sortField] || 0;
      if (sortField === "name") {
        valA = a.name || "";
        valB = b.name || "";
        return sortDirection === "asc" ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }
      return sortDirection === "asc" ? valA - valB : valB - valA;
    });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return "↕️";
    return sortDirection === "asc" ? "↑" : "↓";
  };

  // Show loading spinner only on first load
  if (loading && isInitialLoad) {
    return <div className="spinner">Loading ambassador data...</div>;
  }

  return (
    <div>
      {/* Header Section */}
      <div className="section-header">
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>🏅 Ambassador Program</h2>
          <p style={{ fontSize: 12, color: "var(--text-3)", marginTop: 4 }}>
            Manage ambassadors, track performance, and process withdrawals
          </p>
        </div>
        {canManageAmbassadorFinance && pendingWithdrawals > 0 && (
          <button className="btn btn-primary" onClick={handleBulkApprove}>
            ✓ Bulk Approve ({pendingWithdrawals})
          </button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="stats-grid" style={{ marginBottom: 16 }}>
        <div className="stat-card" style={{ borderLeft: "4px solid #3B82F6" }}>
          <div className="lbl">Total Ambassadors</div>
          <div className="val" style={{ fontSize: 20 }}>{totalAmbassadors}</div>
          <div className="stat-sub">Active ambassadors</div>
        </div>
        <div className="stat-card" style={{ borderLeft: "4px solid #10B981" }}>
          <div className="lbl">Total Earned (All Time)</div>
          <div className="val success">{fmt(totalEarned)}</div>
          <div className="stat-sub">Commissions paid</div>
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: 16 }}>
        <div className="stat-card" style={{ borderLeft: "4px solid #F59E0B" }}>
          <div className="lbl">Available Balance</div>
          <div className="val warning">{fmt(totalAvailable)}</div>
          <div className="stat-sub">Ready for withdrawal</div>
        </div>
        <div className="stat-card" style={{ borderLeft: "4px solid #EF4444" }}>
          <div className="lbl">Pending Withdrawals</div>
          <div className="val danger">{pendingWithdrawals}</div>
          <div className="stat-sub">{fmt(pendingAmount)} awaiting approval</div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="tab-switch">
        <button className={activeTab === "monitor" ? "active" : ""} onClick={() => setActiveTab("monitor")}>📊 Performance</button>
        <button className={activeTab === "leaderboard" ? "active" : ""} onClick={() => setActiveTab("leaderboard")}>🏆 Leaderboard</button>
        <button className={activeTab === "withdrawals" ? "active" : ""} onClick={() => setActiveTab("withdrawals")}>
          💸 Withdrawals {pendingWithdrawals > 0 && <span className="nav-badge" style={{ marginLeft: 4 }}>{pendingWithdrawals}</span>}
        </button>
      </div>

      {/* Performance Tab */}
      {activeTab === "monitor" && (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", borderBottom: "1px solid var(--border)" }}>
            <input
              placeholder="🔍 Search by name or email..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ width: "100%", padding: "8px 12px", border: "1.5px solid var(--border)", borderRadius: 8, fontSize: 13, outline: "none" }}
            />
          </div>
          
          {filteredReport.length === 0 ? (
            <div style={{ textAlign: "center", padding: 48, color: "var(--text-3)" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🏅</div>
              <div>No ambassadors yet. Add them in the Users tab.</div>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="w-full">
                <thead>
                  <tr>
                    <th style={{ cursor: "pointer" }} onClick={() => handleSort("name")}>Ambassador {getSortIcon("name")}</th>
                    <th style={{ cursor: "pointer", textAlign: "center" }} onClick={() => handleSort("orderCount")}>Orders {getSortIcon("orderCount")}</th>
                    <th style={{ cursor: "pointer", textAlign: "center" }} onClick={() => handleSort("walkInCount")}>Walk-ins {getSortIcon("walkInCount")}</th>
                    <th style={{ cursor: "pointer", textAlign: "right" }} onClick={() => handleSort("totalSales")}>Total Sales {getSortIcon("totalSales")}</th>
                    <th style={{ cursor: "pointer", textAlign: "right" }} onClick={() => handleSort("totalEarned")}>Earned {getSortIcon("totalEarned")}</th>
                    <th style={{ textAlign: "right" }}>Available</th>
                    <th style={{ textAlign: "center" }}>Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReport.map(a => (
                    <tr key={a.email}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{a.name || a.email?.split("@")[0]}</div>
                        <div style={{ fontSize: 10, color: "var(--text-3)" }}>{a.email}</div>
                      </td>
                      <td style={{ textAlign: "center", fontWeight: 600 }}>{a.orderCount || 0}</td>
                      <td style={{ textAlign: "center", fontWeight: 600 }}>{a.walkInCount || 0}</td>
                      <td style={{ textAlign: "right", fontWeight: 600 }}>{fmt(a.totalSales || 0)}</td>
                      <td style={{ textAlign: "right", fontWeight: 700, color: "#10B981" }}>{fmt(a.totalEarned || 0)}</td>
                      <td style={{ textAlign: "right", fontWeight: 600, color: a.availableBalance > 0 ? "#F59E0B" : "var(--text-3)" }}>
                        {fmt(a.availableBalance || 0)}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                          {canManageAmbassadorFinance ? <input
                            type="number"
                            min="0"
                            max="100"
                            defaultValue={a.commissionRate || 10}
                            style={{ width: 55, padding: "4px 6px", border: "1px solid var(--border)", borderRadius: 4, fontSize: 11, textAlign: "center" }}
                            onBlur={e => {
                              const newRate = Number(e.target.value);
                              if (newRate !== (a.commissionRate || 10)) {
                                if (window.confirm(`Change commission for ${a.name} from ${a.commissionRate}% to ${newRate}%?\n\nApplies to NEW orders only.`)) {
                                  handleCommission(a.email, newRate);
                                } else {
                                  e.target.value = a.commissionRate || 10;
                                }
                              }
                            }}
                          /> : <span style={{fontSize:11,fontWeight:700}}>{a.commissionRate || 10}</span>}
                          <span style={{ fontSize: 11 }}>%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Leaderboard Tab */}
      {activeTab === "leaderboard" && (
        <div className="card">
          <h2>🏆 Ambassador Leaderboard</h2>
          <Leaderboard data={lbData} currentUserEmail={null} showReset={true} onReset={handleReset} loading={!lbData} />
        </div>
      )}

      {/* Withdrawals Tab */}
      {activeTab === "withdrawals" && (
        <div className="card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "14px 16px", borderBottom: "1px solid var(--border)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
              <h2 style={{ margin: 0 }}>💸 Withdrawal Requests</h2>
              {pendingWithdrawals > 0 && (
                <button className="btn-sm btn-approve" onClick={handleBulkApprove}>✓ Bulk Approve ({pendingWithdrawals})</button>
              )}
            </div>
          </div>
          
          {withdrawals.length === 0 ? (
            <div style={{ textAlign: "center", padding: 48, color: "var(--text-3)" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>💸</div>
              <div>No withdrawal requests yet.</div>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="w-full">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Ambassador</th>
                    <th>Amount</th>
                    <th>Notes</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {withdrawals.map(w => {
                    const isPending = w.status === "Pending";
                    return (
                      <tr key={w.withdrawalId}>
                        <td style={{ fontFamily: "monospace", fontSize: 11 }}>{w.withdrawalId}</td>
                        <td style={{ fontWeight: 600 }}>{w.ambassadorName || w.ambassadorEmail?.split("@")[0]}</td>
                        <td style={{ fontWeight: 700, color: "#10B981" }}>{fmt(w.amount)}</td>
                        <td style={{ fontSize: 11, color: "var(--text-3)" }}>{w.notes || "—"}</td>
                        <td style={{ fontSize: 11 }}>{new Date(w.createdAt).toLocaleDateString()}</td>
                        <td><PayBadge status={w.status} /></td>
                        <td>
                          {canManageAmbassadorFinance && isPending && (
                            <div style={{ display: "flex", gap: 4 }}>
                              <button 
                                className="btn-sm btn-approve" 
                                onClick={() => handleWd(w.withdrawalId, "approve")}
                                disabled={processingId === w.withdrawalId}
                              >
                                {processingId === w.withdrawalId ? "..." : "✓ Pay"}
                              </button>
                              <button 
                                className="btn-sm btn-reject" 
                                onClick={() => handleWd(w.withdrawalId, "reject")}
                                disabled={processingId === w.withdrawalId}
                              >
                                ✕
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AmbassadorMonitor;